import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Database, ChevronLeft, Download, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminBackups() {
  const { api } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      const response = await api.get('/admin/backups');
      setBackups(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await api.post('/admin/backups');
      toast.success('Backup criado com sucesso');
      fetchBackups();
    } catch (error) {
      toast.error('Erro ao criar backup');
    } finally {
      setCreating(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Em progresso', className: 'bg-amber-100 text-amber-700', icon: RefreshCw },
      completed: { label: 'Concluído', className: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      failed: { label: 'Falhou', className: 'bg-red-100 text-red-700', icon: AlertCircle },
    };
    const { label, className, icon: Icon } = statusMap[status] || statusMap.pending;
    return (
      <Badge className={className}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-backups">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Backups</h1>
            <p className="text-slate-600 mt-1">Gerenciar backups da base de dados</p>
          </div>
          <Button 
            onClick={handleCreateBackup} 
            className="bg-primary" 
            disabled={creating}
            data-testid="create-backup-btn"
          >
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              <>
                <Database className="w-4 h-4 mr-2" />
                Novo Backup
              </>
            )}
          </Button>
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-amber-800 text-sm">
            <strong>Atenção:</strong> Os backups contêm dados sensíveis. Mantenha-os seguros e com acesso restrito.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Arquivo</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Data</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Tamanho</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                    Carregando...
                  </td>
                </tr>
              ) : backups.length > 0 ? (
                backups.map((backup) => (
                  <tr key={backup.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-slate-400" />
                        <span className="font-medium text-slate-900">{backup.filename}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-600">
                        {format(new Date(backup.created_at), 'dd/MM/yyyy HH:mm')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-600">{formatFileSize(backup.file_size)}</span>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(backup.status)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                    <Database className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p>Nenhum backup encontrado</p>
                    <p className="text-sm mt-1">Clique em "Novo Backup" para criar o primeiro</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
